/**
 * Which uploaded files open in the reader's browser, and as what.
 *
 * Read on both sides of an assessment answer's files. The server signs a file
 * for viewing only when this names a kind for it, and serves it under the type
 * named here rather than the one its upload declared: that type is whatever
 * the expert's browser sent, so a page named `notes.pdf` is served as a PDF
 * and fails to open as one instead of running.
 *
 * A name's extension decides on its own, because browsers send no type, or
 * `application/octet-stream`, for files they do not recognise (a `.md` from
 * most of them). The stored type decides only for a name with no extension.
 */

export type FilePreviewKind =
  | "pdf"
  | "image"
  | "video"
  | "audio"
  | "markdown"
  | "text"
  | "docx";

export interface FilePreview {
  kind: FilePreviewKind;
  /** The type the file is served under when it is opened in the browser. */
  contentType: string;
}

const PLAIN_TEXT = "text/plain; charset=utf-8";

const DOCX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// Source, data and markup alike are shown as their text, never rendered:
// `.html` and `.svg` are here so a reader sees the markup, not the page.
const TEXT_EXTENSIONS = [
  "txt",
  "text",
  "log",
  "csv",
  "tsv",
  "json",
  "jsonl",
  "ndjson",
  "yaml",
  "yml",
  "toml",
  "xml",
  "ini",
  "cfg",
  "conf",
  "env",
  "tex",
  "rst",
  "html",
  "htm",
  "svg",
  "css",
  "scss",
  "sass",
  "less",
  "js",
  "mjs",
  "cjs",
  "jsx",
  "ts",
  "mts",
  "cts",
  "tsx",
  "vue",
  "svelte",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "kt",
  "kts",
  "scala",
  "swift",
  "dart",
  "c",
  "h",
  "cc",
  "cpp",
  "cxx",
  "hpp",
  "hh",
  "cs",
  "php",
  "pl",
  "lua",
  "r",
  "jl",
  "ex",
  "exs",
  "erl",
  "hs",
  "clj",
  "elm",
  "sol",
  "sh",
  "bash",
  "zsh",
  "fish",
  "ps1",
  "bat",
  "sql",
  "graphql",
  "gql",
  "proto",
  "tf",
  "hcl",
  "gradle",
  "diff",
  "patch",
] as const;

const BY_EXTENSION: ReadonlyMap<string, FilePreview> = new Map<
  string,
  FilePreview
>([
  ["pdf", { kind: "pdf", contentType: "application/pdf" }],
  ["png", { kind: "image", contentType: "image/png" }],
  ["jpg", { kind: "image", contentType: "image/jpeg" }],
  ["jpeg", { kind: "image", contentType: "image/jpeg" }],
  ["gif", { kind: "image", contentType: "image/gif" }],
  ["webp", { kind: "image", contentType: "image/webp" }],
  ["avif", { kind: "image", contentType: "image/avif" }],
  ["mp4", { kind: "video", contentType: "video/mp4" }],
  ["m4v", { kind: "video", contentType: "video/mp4" }],
  ["webm", { kind: "video", contentType: "video/webm" }],
  ["mov", { kind: "video", contentType: "video/quicktime" }],
  ["mp3", { kind: "audio", contentType: "audio/mpeg" }],
  ["m4a", { kind: "audio", contentType: "audio/mp4" }],
  ["wav", { kind: "audio", contentType: "audio/wav" }],
  ["ogg", { kind: "audio", contentType: "audio/ogg" }],
  ["oga", { kind: "audio", contentType: "audio/ogg" }],
  ["opus", { kind: "audio", contentType: "audio/ogg" }],
  ["aac", { kind: "audio", contentType: "audio/aac" }],
  ["flac", { kind: "audio", contentType: "audio/flac" }],
  ["md", { kind: "markdown", contentType: PLAIN_TEXT }],
  ["markdown", { kind: "markdown", contentType: PLAIN_TEXT }],
  ["docx", { kind: "docx", contentType: DOCX_CONTENT_TYPE }],
  ...TEXT_EXTENSIONS.map((extension): [string, FilePreview] => [
    extension,
    { kind: "text", contentType: PLAIN_TEXT },
  ]),
]);

const BY_CONTENT_TYPE: ReadonlyMap<string, FilePreview> = new Map<
  string,
  FilePreview
>([
  ["application/pdf", { kind: "pdf", contentType: "application/pdf" }],
  ["image/png", { kind: "image", contentType: "image/png" }],
  ["image/jpeg", { kind: "image", contentType: "image/jpeg" }],
  ["image/gif", { kind: "image", contentType: "image/gif" }],
  ["image/webp", { kind: "image", contentType: "image/webp" }],
  ["image/avif", { kind: "image", contentType: "image/avif" }],
  ["video/mp4", { kind: "video", contentType: "video/mp4" }],
  ["video/webm", { kind: "video", contentType: "video/webm" }],
  ["video/quicktime", { kind: "video", contentType: "video/quicktime" }],
  ["audio/mpeg", { kind: "audio", contentType: "audio/mpeg" }],
  ["audio/mp4", { kind: "audio", contentType: "audio/mp4" }],
  ["audio/wav", { kind: "audio", contentType: "audio/wav" }],
  ["audio/x-wav", { kind: "audio", contentType: "audio/wav" }],
  ["audio/wave", { kind: "audio", contentType: "audio/wav" }],
  ["audio/ogg", { kind: "audio", contentType: "audio/ogg" }],
  ["audio/aac", { kind: "audio", contentType: "audio/aac" }],
  ["audio/flac", { kind: "audio", contentType: "audio/flac" }],
  ["text/markdown", { kind: "markdown", contentType: PLAIN_TEXT }],
  ["text/x-markdown", { kind: "markdown", contentType: PLAIN_TEXT }],
  ["application/json", { kind: "text", contentType: PLAIN_TEXT }],
  ["application/xml", { kind: "text", contentType: PLAIN_TEXT }],
  [DOCX_CONTENT_TYPE, { kind: "docx", contentType: DOCX_CONTENT_TYPE }],
]);

/** Lower case, without the dot; empty when the name has none. */
export function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : "";
}

/** How the file opens in a browser, or null when it can only be downloaded. */
export function filePreviewOf(file: {
  fileName: string;
  mimeType: string;
}): FilePreview | null {
  const extension = fileExtension(file.fileName);
  if (extension) {
    return BY_EXTENSION.get(extension) ?? null;
  }
  // The declared type with its parameters cut off: `text/plain;charset=x`.
  const declared = file.mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
  const known = BY_CONTENT_TYPE.get(declared);
  if (known) {
    return known;
  }
  return declared.startsWith("text/")
    ? { kind: "text", contentType: PLAIN_TEXT }
    : null;
}
